// Navigation Item Component
// Individual navigation item with hover effects and active states

import React from 'react';
import { useTheme } from '../../design-system/ThemeProvider';
import { NavigationIcon } from './NavigationIcon';
import { NavigationBadge } from './NavigationBadge';
import { NavigationItem as NavigationItemType } from './types';

interface NavigationItemProps {
  item: NavigationItemType;
  isActive: boolean;
  isExpanded?: boolean;
  hasChildren?: boolean;
  depth?: number;
  onClick: () => void;
  onToggle?: () => void;
}

export const NavigationItem: React.FC<NavigationItemProps> = ({
  item,
  isActive,
  isExpanded = false,
  hasChildren = false,
  depth = 0,
  onClick,
  onToggle
}) => {
  const { theme } = useTheme();

  const getItemStyles = () => {
    const baseStyles = {
      display: 'flex',
      alignItems: 'center',
      padding: `${theme.componentSpacing.navigation.itemPadding} ${theme.spacing[4]}`,
      marginBottom: theme.spacing[1],
      borderRadius: theme.borderRadius.lg,
      cursor: 'pointer',
      transition: theme.transitions.duration.fast,
      textDecoration: 'none',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      marginLeft: depth > 0 ? theme.spacing[4] : '0'
    };

    if (isActive) {
      return {
        ...baseStyles,
        backgroundColor: theme.componentTokens.navigation.item.backgroundActive,
        color: theme.componentTokens.navigation.item.textActive,
        fontWeight: theme.typography.fontWeight.semibold
      };
    }

    return {
      ...baseStyles,
      backgroundColor: theme.componentTokens.navigation.item.background,
      color: theme.componentTokens.navigation.item.text
    };
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (hasChildren && onToggle) {
      onToggle();
    } else {
      onClick();
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive) {
      e.currentTarget.style.backgroundColor = theme.componentTokens.navigation.item.backgroundHover;
      e.currentTarget.style.color = theme.componentTokens.navigation.item.textHover;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive) {
      e.currentTarget.style.backgroundColor = theme.componentTokens.navigation.item.background;
      e.currentTarget.style.color = theme.componentTokens.navigation.item.text;
    }
  };

  return (
    <div
      style={getItemStyles()}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Icon */}
      {item.icon && (
        <div style={{ marginRight: theme.componentSpacing.navigation.iconGap }}>
          <NavigationIcon
            name={item.icon}
            size={18}
            color={isActive ? theme.componentTokens.navigation.item.textActive : 'currentColor'}
          />
        </div>
      )}

      {/* Label */}
      <span style={{ flex: 1 }}>{item.label}</span>

      {/* Badge */}
      {item.badge && (
        <div style={{ marginLeft: theme.spacing[2] }}>
          <NavigationBadge
            content={item.badge}
            variant={item.badge === 'live' ? 'success' : 'default'}
          />
        </div>
      )}

      {/* Expand/Collapse Icon */}
      {hasChildren && (
        <div style={{ marginLeft: theme.spacing[2] }}>
          <NavigationIcon
            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size={16}
            color="currentColor"
          />
        </div>
      )}
    </div>
  );
};